import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";

import { restFetcher, restPutter } from "~/api/fetchers";

import {
  CodeType,
  PRIMHDCode,
  PrimhdRecord,
  PrimhdRecordSummary,
  PrimhdReferral,
  PrimhdReferralPage,
  UpdateReferralRequest,
  UpdateReferralResponse,
  WorkerErrorSummary,
} from "./types";

export type PrimhdCode = {
  code: string;
  description: string;
  endDate?: string;
};

export type PrimhdCodeQueryResponse = Record<CodeType, PrimhdCode[]>;

export const primhdCodeQueries = {
  all: () => ["PrimhdReferralCodes"],
  lists: () => [...primhdCodeQueries.all(), "list"],
  list: (filters: string) =>
    queryOptions({
      queryKey: [...primhdCodeQueries.lists(), filters],
      queryFn: () => restFetcher<PrimhdCodeQueryResponse>(`/v2/primhd/codes?${filters}`),
      staleTime: Infinity,
    }),
};

export type PaginatedResponse = {
  currentPageNumber: number;
  totalPages: number;
  totalResults: number;
  pageSize: number;
  currentPageTotal: number;
};

type PrimhdRecordApiResponse = {
  results: PrimhdRecordSummary[];
} & PaginatedResponse;

export const recordQueries = {
  all: () => ["PrimhdRecord"],
  lists: () => [...recordQueries.all(), "list"],
  list: (filters: string) =>
    queryOptions({
      queryKey: [...recordQueries.lists(), filters],
      queryFn: () => {
        const searchParams = new URLSearchParams(filters);
        searchParams.append("status", "REJECTED,WARNING");

        return restFetcher<PrimhdRecordApiResponse>(
          "/v2/primhd/records?" + searchParams.toString()
        );
      },
    }),
  details: () => [...recordQueries.all(), "detail"],
  detail: (recordSummary: PrimhdRecordSummary) =>
    queryOptions({
      queryKey: [...recordQueries.details(), recordSummary.type, recordSummary.id],
      queryFn: () =>
        restFetcher<PrimhdRecord>(
          `/v1/primhd/records/${recordSummary.type.toLocaleLowerCase()}/${recordSummary.id}`
        ),
      staleTime: Infinity,
    }),
  summary: () =>
    queryOptions({
      queryKey: [...recordQueries.all(), "summary"],
      queryFn: () => restFetcher("/v2/primhd/records/summary") as Promise<WorkerErrorSummary[]>,
    }),
};

type ReferralUpdateRequest = {
  referralId: number;
  body: UpdateReferralRequest;
};

export const referralQueries = {
  all: () => ["PrimhdReferral"],
  lists: () => ({
    queryKey: [...referralQueries.all(), "list"],
  }),
  list: (filters: string) =>
    queryOptions({
      queryKey: [...referralQueries.lists().queryKey, filters],
      queryFn: () => restFetcher<PrimhdReferralPage>(`/v2/primhd/referrals?${filters}`),
      placeholderData: keepPreviousData,
    }),
  details: () => [...referralQueries.all(), "detail"],
  detail: (id: number) =>
    queryOptions({
      queryKey: [...referralQueries.details(), id],
      queryFn: () => restFetcher<PrimhdReferral>(`/v2/primhd/referrals/${id}`),
    }),
  update: () => ({
    mutationFn: (request: ReferralUpdateRequest) =>
      restPutter<UpdateReferralRequest, UpdateReferralResponse>(
        `/v1/primhd/referrals/${request.referralId}`,
        request.body
      ),
  }),
};

export const useGetPrimhdCodesByType = (type: CodeType) => {
  const endpoint = `/v1/primhd/codes/clinic?type=${type}`;
  return useQuery({
    queryKey: [endpoint],
    queryFn: () => restFetcher<PRIMHDCode[]>(endpoint),
  });
};
